import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { EntitlementService } from '../../core/services/entitlement.service';
import { SubscriptionStore } from '../../core/stores/subscription.store';

interface UsageRow {
  label: string;
  key: 'members' | 'activeEvents' | 'users' | 'paymentsThisMonth' | 'ledgerEntriesThisYear' | 'customFieldDefs';
}

@Component({
  selector: 'app-subscription-usage-card',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div class="flex items-center justify-between mb-3">
        <div>
          <p class="text-xs text-gray-500 font-medium">Plan usage</p>
          <p class="text-sm font-semibold text-gray-900">{{ store.planName() ?? 'No plan' }}</p>
        </div>
        <a routerLink="/billing"
           class="text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
          Manage
        </a>
      </div>

      @if (rows().length) {
        <div class="space-y-2.5">
          @for (row of rows(); track row.key) {
            <div>
              <div class="flex justify-between text-xs mb-0.5">
                <span class="text-gray-600">{{ row.label }}</span>
                <span class="text-gray-700 font-medium">
                  {{ ent.metric(row.key)?.current ?? 0 }} /
                  {{ ent.metric(row.key)?.max ?? '∞' }}
                </span>
              </div>
              <div class="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full transition-all"
                     [class.bg-emerald-500]="bandFor(row.key) === 'ok'"
                     [class.bg-amber-500]="bandFor(row.key) === 'warn'"
                     [class.bg-rose-500]="bandFor(row.key) === 'over'"
                     [style.width.%]="ent.percentUsed(row.key)"></div>
              </div>
            </div>
          }
        </div>
      } @else {
        <p class="text-xs text-gray-500">No usage data yet.</p>
      }
    </div>
  `
})
export class SubscriptionUsageCardComponent {
  ent = inject(EntitlementService);
  store = inject(SubscriptionStore);

  readonly rows = computed<UsageRow[]>(() => {
    if (!this.ent.usage()) return [];
    return [
      { label: 'Members',         key: 'members' },
      { label: 'Active events',   key: 'activeEvents' },
      { label: 'Users',           key: 'users' },
      { label: 'Payments / month', key: 'paymentsThisMonth' }
    ];
  });

  bandFor(key: UsageRow['key']): 'ok' | 'warn' | 'over' {
    const p = this.ent.percentUsed(key);
    if (p >= 100) return 'over';
    if (p >= 80) return 'warn';
    return 'ok';
  }
}
